import { useQuery } from '@tanstack/react-query'
import type {
  AccessAction,
  FullAccessLog,
  ListAccessLogsResult,
  MaskedAccessLog,
} from '@terafile/shared'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { downloadAccessLogsCsv, listAccessLogs } from '../lib/access-logs-api.js'
import { FETCH_ERROR_FALLBACK, SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { classifyFile, iconForKind } from '../lib/file-kind.js'
import { truncateIp } from '../lib/ip-display.js'
import { ACCESS_ACTION_LABEL, ACTOR_TYPE_LABEL, LOADING } from '../lib/labels.js'

export function AccessLogsPage() {
  const { id: projectId } = useParams<{ id: string }>()
  const [actionFilter, setActionFilter] = useState<AccessAction | ''>('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['access-logs', projectId, actionFilter],
    queryFn: () => listAccessLogs(projectId ?? '', actionFilter ? { action: actionFilter } : {}),
    enabled: !!projectId,
  })

  const onDownloadCsv = async () => {
    if (!projectId) return
    try {
      const blob = await downloadAccessLogsCsv(
        projectId,
        actionFilter ? { action: actionFilter } : {},
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `access-logs-${projectId}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
      <h1 className="text-xl font-semibold tracking-tight">アクセスログ</h1>
      {isLoading && <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {toUserMessage(error, FETCH_ERROR_FALLBACK)}
        </div>
      )}
      {data && (
        <LogsView
          data={data}
          actionFilter={actionFilter}
          setActionFilter={setActionFilter}
          onDownloadCsv={onDownloadCsv}
        />
      )}
    </div>
  )
}

function LogsView({
  data,
  actionFilter,
  setActionFilter,
  onDownloadCsv,
}: {
  data: ListAccessLogsResult
  actionFilter: AccessAction | ''
  setActionFilter: (v: AccessAction | '') => void
  onDownloadCsv: () => void
}) {
  const isMasked = data.mode === 'masked'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label htmlFor="action-filter" className="text-sm">
            操作種別:
          </label>
          <select
            id="action-filter"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value as AccessAction | '')}
            className="px-3 py-1.5 border border-border rounded-md text-sm bg-background"
          >
            <option value="">すべて</option>
            {Object.entries(ACCESS_ACTION_LABEL).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </div>
        {!isMasked && (
          <button
            type="button"
            onClick={onDownloadCsv}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium"
          >
            CSV ダウンロード
          </button>
        )}
      </div>

      {isMasked && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm">
          <p className="font-medium text-yellow-900">
            無料プランでは、個人を特定できる情報の一部が伏せ字（****）になります
          </p>
          <p className="text-xs text-yellow-900 mt-1">
            視聴した方やアクセスしたブラウザ／端末の詳細は、有料プランでご確認いただけます。
          </p>
        </div>
      )}

      <div className="overflow-x-auto border border-border rounded-md">
        {/* min-w を与えて狭い画面では横スクロール（w-full だけだとセルが縦に潰れる）。 */}
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="text-left px-3 py-2">日時</th>
              <th className="text-left px-3 py-2">操作</th>
              <th className="text-left px-3 py-2">操作した人</th>
              <th className="text-left px-3 py-2">ファイル</th>
              <th className="text-left px-3 py-2">視聴した人</th>
              <th className="text-left px-3 py-2">
                <span
                  title="アクセスしたインターネット回線の地域がわかる程度の情報です"
                  className="cursor-help underline decoration-dotted decoration-muted-foreground/40"
                >
                  アクセス元
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                  まだアクセス記録はありません。共有URLを発行して相手に送ると、ここに記録が残ります。
                </td>
              </tr>
            ) : (
              data.items.map((log) => (
                <tr key={log.id} className="border-t border-border">
                  <td className="px-3 py-2 whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString('ja-JP')}
                  </td>
                  <td className="px-3 py-2">{ACCESS_ACTION_LABEL[log.action] ?? log.action}</td>
                  <td className="px-3 py-2">{ACTOR_TYPE_LABEL[log.actorType] ?? log.actorType}</td>
                  <td className="px-3 py-2 text-xs">
                    <TargetCell log={log} />
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {log.viewerEmail ?? log.viewerAnonymousId ?? '—'}
                  </td>
                  <td
                    className="px-3 py-2 text-xs"
                    title="アクセスしたインターネット回線の地域がわかる程度の情報です"
                  >
                    {truncateIp(log.ip)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        計 {data.total} 件 · {data.page} / {Math.max(1, Math.ceil(data.total / data.limit))} ページ
      </p>
    </div>
  )
}

/**
 * 「ファイル」列の表示。状況に応じて分かりやすい文言＋アイコンに切り替える。
 *
 * - ファイル名が解決できる    → 種別アイコン + ファイル名（プロジェクト詳細へのリンク）
 * - fileId はあるが name が消えた → 「(削除済みファイル) <短縮id>」
 * - fileId なし & shareLinkId あり → 「共有URL着地」（ゲストが URL を踏んだ瞬間）
 * - fileId なし & projectId あり → 「プロジェクト操作」（DELETE 等）
 * - 何も無い                 → 「—」
 */
function TargetCell({ log }: { log: FullAccessLog | MaskedAccessLog }) {
  if (log.fileName) {
    const kind = classifyFile(log.fileMimeType ?? '')
    const content = (
      <>
        <span aria-hidden className="text-base leading-none">
          {iconForKind(kind)}
        </span>
        <span className="truncate max-w-[240px]" title={log.fileName}>
          {log.fileName}
        </span>
      </>
    )
    // 操作対象のファイルが残っていればプロジェクト詳細へリンク。
    // ファイル個別の選択 UI はないが、プロジェクトを開けばその場でファイル一覧から探せる。
    return log.projectId ? (
      <Link
        to={`/projects/${log.projectId}`}
        className="inline-flex items-center gap-1.5 hover:underline underline-offset-4"
      >
        {content}
      </Link>
    ) : (
      <span className="inline-flex items-center gap-1.5">{content}</span>
    )
  }
  if (log.fileId) {
    return <span className="text-muted-foreground italic">(削除済みファイル)</span>
  }
  if (log.shareLinkId) {
    return <span className="text-muted-foreground">共有URL着地</span>
  }
  if (log.projectId) {
    return <span className="text-muted-foreground">プロジェクト操作</span>
  }
  return <span>—</span>
}
